import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { govardDarkTheme, govardLightTheme } from './antdTheme.js';
import { ensureThemeVarsInjected } from './themeVars.js';
import type { ThemeConfig } from 'antd';

export type ThemeMode = 'light' | 'dark' | 'auto';
export type ActualTheme = 'light' | 'dark';

interface ThemeContextValue {
  mode: ThemeMode;
  actualTheme: ActualTheme;
  setMode: (mode: ThemeMode) => void;
  antdTheme: ThemeConfig;
}

const STORAGE_KEY = 'govard_crm_theme';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getSystemTheme(): ActualTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function getStoredMode(): ThemeMode {
  if (
    typeof window === 'undefined' ||
    typeof localStorage === 'undefined' ||
    !localStorage?.getItem
  ) {
    return 'auto';
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'auto') {
      return stored;
    }
  } catch {
    return 'auto';
  }
  return 'auto';
}

export function ThemeProvider({ children }: { children: ReactNode }): ReactElement {
  const [mode, setModeState] = useState<ThemeMode>(() => getStoredMode());
  const [systemTheme, setSystemTheme] = useState<ActualTheme>(() => getSystemTheme());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      if (!mediaQuery) return;
      const handleChange = (e: MediaQueryListEvent) => {
        setSystemTheme(e.matches ? 'dark' : 'light');
      };

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
      if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    } catch {
      // ignore
    }
    return undefined;
  }, []);

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
    if (
      typeof window !== 'undefined' &&
      typeof localStorage !== 'undefined' &&
      localStorage?.setItem
    ) {
      try {
        localStorage.setItem(STORAGE_KEY, newMode);
      } catch {
        // ignore
      }
    }
  };

  const actualTheme: ActualTheme = mode === 'auto' ? systemTheme : mode;
  const antdTheme = actualTheme === 'dark' ? govardDarkTheme : govardLightTheme;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    ensureThemeVarsInjected(document);
    const root = document.documentElement;
    root.setAttribute('data-theme', actualTheme);
    root.style.colorScheme = actualTheme;

    if (actualTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [actualTheme]);

  return (
    <ThemeContext.Provider value={{ mode, actualTheme, setMode, antdTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    return {
      mode: 'light',
      actualTheme: 'light',
      setMode: () => undefined,
      antdTheme: govardLightTheme,
    };
  }
  return context;
}
